import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  animationFrameScheduler,
  combineLatest,
  interval,
} from 'rxjs';
import {
  distinctUntilChanged,
  endWith,
  map,
  switchMap,
  takeWhile,
} from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as alerts from '../../_functions/alerts';
import { FieldConfig } from '../../_models/field.interface';
import { AuthService } from '../../_services/auth.service';
import { FidelidadeService } from '../../_services/fidelidade.service';
import { ValidacaoService } from '../../_services/validacao.service';
import { NumberPipe } from '../../_shared/pipes/number.pipe';

const easeOutQuad = (x: number): number => x * (2 - x);

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [NumberPipe],
})
export class HomeComponent implements OnInit {
  readonly CAMPO_SENHA: string = 'senha';
  readonly CAMPO_CONFIRMACAO_SENHA: string = 'confirmacaoSenha';
  readonly ICONE_OLHO_ABERTO: string = 'assets/images/icons/olho_aberto.png';
  readonly ICONE_OLHO_FECHADO: string = 'assets/images/icons/olho_fechado.png';

  exibirLoader: boolean = false;
  exibirAcesso: boolean = true;
  exibirCadastro: boolean = false;
  sessaoApp: boolean = false;

  mensagemLogin: string = '';
  acessoForm: UntypedFormGroup;

  etapas: number[] = [1, 2, 3, 4, 5];
  etapaCadastro: number = 1;
  cadastroForm: UntypedFormGroup;

  regConfigPopupTermos: FieldConfig[] = [];
  regConfigPopupRegulamento: FieldConfig[] = [];

  exibePopupGenerico: boolean = false;
  tituloModalGenerico: string = '';
  descricaoModalGenerico: string[] = [];

  // Contador de membros ativos
  contador = new BehaviorSubject(0);
  duracao = new BehaviorSubject(20000);

  entrando: boolean = false;
  cadastrando: boolean = false;

  //
  buscandoMembro: boolean = false;

  //
  tipoCampoSenha: boolean = false;
  tipoCampoConfirmacaoSenha: boolean = false;
  iconeSenha: string = this.ICONE_OLHO_FECHADO;
  iconeConfirmacaoSenha: string = this.ICONE_OLHO_FECHADO;

  precisaAprovador: boolean = false;
  aprovadorValido: boolean = false;

  hashIndicacao: string = '';
  origem: number = 0;
  linkRedirect: string = '';
  returnUrl: string = '';

  private readonly contagemAtual = combineLatest([
    this.contador,
    this.duracao,
  ]).pipe(
    switchMap(([contador, duracao]) => {
      const startTime = animationFrameScheduler.now();

      return interval(0, animationFrameScheduler).pipe(
        map(() => animationFrameScheduler.now() - startTime),
        map((tempoRestante) => tempoRestante / duracao),
        takeWhile((progress) => progress <= 1),
        map(easeOutQuad),
        map((progress: any) => Math.round(progress * contador)),
        endWith(contador),
        distinctUntilChanged()
      );
    })
  );

  quantidadeLovers: any[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private fidelidadeService: FidelidadeService,
    private router: Router,
    private validacaoService: ValidacaoService,
    private numberPipe: NumberPipe,
    private route: ActivatedRoute
  ) {
    this.acessoForm = this.formBuilder.group({
      cpf: ['', [Validators.required, Validators.minLength(11)]],
      senha: ['', [Validators.required]],
    });

    this.cadastroForm = this.formBuilder.group({
      cpf: ['', [Validators.required, Validators.minLength(11)]],
      nome: ['', [Validators.required]],
      sobrenome: ['', [Validators.required]],
      aceiteTermos: [false],
      dataNascimento: ['', [Validators.required, Validators.minLength(8)]],
      email: ['', [Validators.required, Validators.email]],
      celular: ['', [Validators.required, Validators.minLength(11)]],
      senha: [
        '',
        [
          Validators.required,
          Validators.pattern(
            '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[A-Za-z\\d$@$!%*?&].{7,}$'
          ),
        ],
      ],
      confirmarSenha: [
        '',
        [
          Validators.required,
          Validators.pattern(
            '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[A-Za-z\\d$@$!%*?&].{7,}$'
          ),
        ],
      ],
      cpfResponsavel: ['', [Validators.minLength(11)]],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params.hashIndicacao) {
        this.hashIndicacao = params.hashIndicacao;
        this.irParaCadastro();
      }

      if (params.origem) {
        this.origem = params.origem;
        this.irParaCadastro();
      }

      if (params.redirectLink) this.linkRedirect = atob(params.redirectLink);

      if (params.returnUrl) this.returnUrl = params.returnUrl;
    });

    this.iniciarContador();
  }

  private iniciarContador(): void {
    this.contadorSubscribe();
    this.obterTotalMembros();
  }

  private obterTotalMembros(): void {
    this.fidelidadeService.obterTotalMembrosAtivos().subscribe({
      next: (res: any) => this.processarSucessoObterTotalMembrosAtivos(res),
      error: (err: any) => this.processarFalhaObterTotalMembrosAtivos(err),
    });
  }

  private processarSucessoObterTotalMembrosAtivos(res: any): void {
    this.contador.next(res);
  }

  private processarFalhaObterTotalMembrosAtivos(err: any): void {
    console.error(
      'Ocorreu um erro ao obter a quantidade de membros ativos no programa',
      err
    );
    this.contador.next(21428492);
  }

  private contadorSubscribe(): void {
    this.contagemAtual.subscribe((valorAtual) => {
      const valorAtualFormatado = this.numberPipe.transform(valorAtual);

      const valorFinal = [];
      for (let i = 0; i < valorAtualFormatado.length; i++) {
        const valor: string = valorAtualFormatado[i].toString();
        let contadorValor = {
          valor: valor,
          tipo: 'numero',
        };

        if (valor === '.') contadorValor.tipo = 'separador';

        valorFinal.push(contadorValor);
      }

      this.quantidadeLovers = valorFinal;
    });
  }

  resetarAcessoForm() {
    this.acessoForm = this.formBuilder.group({
      cpf: ['', [Validators.required, Validators.minLength(11)]],
      senha: ['', [Validators.required]],
    });
  }

  resetarCadastroForm() {
    this.cadastroForm = this.formBuilder.group({
      cpf: ['', [Validators.required, Validators.minLength(11)]],
      nome: ['', [Validators.required]],
      sobrenome: ['', [Validators.required]],
      aceiteTermos: [false],
      dataNascimento: ['', [Validators.required, Validators.minLength(8)]],
      email: ['', [Validators.required, Validators.email]],
      celular: ['', [Validators.required, Validators.minLength(11)]],
      senha: ['', [Validators.required]],
      confirmarSenha: ['', [Validators.required]],
    });
  }

  acessar() {
    const form = this.acessoForm.value;

    this.entrando = true;

    this.mensagemLogin = '';

    this.authService.loginComSenha(form.cpf, form.senha).subscribe(
      (sucesso) => {
        this.processarSucesso(sucesso);
      },
      (falha) => {
        this.processarFalha(falha);
      }
    );
  }

  processarSucesso(response: any) {
    this.entrando = false;
    this.authService.LocalStorage.salvarDadosLocaisUsuario(response);

    this.fidelidadeService.obterMembro().subscribe(
      sucesso => {
        this.authService.LocalStorage.salvarMembro(sucesso);
        this.processarSucessoObterMembro(sucesso);
      }
    );
  }

  processarSucessoObterMembro(res: any) {
    if (
      !res.telefoneValidado &&
      !res.emailValidado &&
      this.emailEObrigatorio(res)
    ) {
      this.authService.LocalStorage.salvarDadosValidados(false);
      this.authService.LocalStorage.salvarRedirectLink(this.linkRedirect);
    } else {
      this.authService.LocalStorage.salvarDadosValidados(true);
      this.authService.LocalStorage.salvarRedirectLink('');

      if (this.linkRedirect !== '') {
        this.router.navigateByUrl(this.linkRedirect);
        return;
      } else if (this.returnUrl !== '') {
        this.router.navigate([this.returnUrl]);
        return;
      }
    }

    this.router.navigate(['/index']);
  }

  processarFalhaObterMembro(err: any) {
    console.error('Ocorreu um erro ao obter o membro', err);
  }

  emailEObrigatorio(membro: any) {
    const dataNascimentoMinima = new Date(1753, 0, 1, 0, 0, 0);

    const nascimento: string = membro.dataNascimento;
    const ano = Number(nascimento.substring(0, 4));
    const mes = Number(nascimento.substring(5, 7));
    const dia = Number(nascimento.substring(8, 10));

    var dataFinal = `${ano}/${mes}/${dia}`;

    const nascimentoDate: Date = new Date(dataFinal);

    if (dataNascimentoMinima.toISOString() == nascimentoDate.toISOString())
      return true;

    let age = this.getAge(nascimentoDate);

    if (age >= 65) return false;
    else return true;
  }

  processarFalha(fail: any) {
    this.mensagemLogin = '';
    this.entrando = false;

    if (fail.erro == 0 || fail == 'Registro não encontrado') {
      this.mensagemLogin = `
        Poxaa, você ainda *não se cadastrou* em nossa base, mas não se preocupe,
        temos um *cadastro rapido*, basta clicar em ´*"Ainda não é um Cacau Lover? Cadastre-se"*´`;
    } else if (fail.erro == 1) {
      this.mensagemLogin = `
        Obaaa, ´*você já é um cacau lover*´ porém falta apenas uma etapa para seguir adiante.
        Clique em ´*Esqueci Minha Senha*´ para finalizar seu cadastro!`;
    } else if (fail == 'CPF Bloqueado') {
      alerts.exibirToast('error', fail);
    } else {
      alerts.exibirToast('error', 'Usuário ou senha inválidos!');
    }
  }

  irParaEsqueciSenha() {
    this.router.navigate(['/esquecisenha']);
  }

  irParaCadastro() {
    this.resetarAcessoForm();
    this.exibirAcesso = false;
    this.exibirCadastro = true;
    this.etapaCadastro = 1;
  }

  irParaAtendimento() {
    this.router.navigate(['/ajuda/cacaulovers']);
  }

  irParaAcesso() {
    if (this.etapaCadastro === 1 || this.etapaCadastro === 6) {
      this.resetarCadastroForm();
      this.etapaCadastro = 1;
      this.exibirAcesso = true;
      this.exibirCadastro = false;
    } else {
      this.etapaCadastro -= 1;
    }
  }

  irParaEtapa(etapa: number) {
    let membro = this.cadastroForm.value;

    if (etapa === 2) {
      if (!this.validacaoService.validarCPF(membro.cpf)) {
        alerts.exibirToast(
          'warning',
          'Informe um CPF válido para prosseguir com o cadastro.'
        );
        return;
      }

      this.buscandoMembro = true;

      this.fidelidadeService.verificarSeJaEstaCadastrado(membro.cpf).subscribe({
        next: (res: any) => {
          if (res.message === 'CacauLover - Cliente cadastrado na base!') {
            alerts.exibirToast(
              'info',
              'Cliente já cadastrado como Cacau Lovers!'
            );
          } else if (
            res.message ===
            'CacauLover - Seu cadastro está pendente de aprovação!'
          ) {
            alerts.exibirToast(
              'info',
              'Seu cadastro está pendente de aprovação!'
            );
          } else if (res.message === 'CacauLover - CPF Bloqueado!') {
            alerts.exibirToast('error', 'CPF Bloqueado');
          }
          this.buscandoMembro = false;
          return;
        },
        error: (err: any) => {
          console.warn(
            'Ocorreu um erro para verificar a existência do cadastro ' +
            membro.cpf +
            ' ',
            err
          );
          this.buscandoMembro = false;
          this.etapaCadastro = etapa;
          return;
        },
      });
      return;
    }

    if (etapa === 3) {
      const nome = `${membro.nome.trim()} ${membro.sobrenome.trim()}`;
      if (nome.length < 5) {
        this.etapaCadastro = 2;
        alerts.exibirToast(
          'warning',
          'O nome e sobrenome informado não é válido!'
        );
        return;
      }
    }

    if (etapa === 4) {
      const nascimento: string = membro.dataNascimento;
      const ano = Number(nascimento.substring(4, 8));

      let erroDataNascimento: boolean = false;

      if (ano.toString().length < 4) {
        alerts.exibirToast('warning', 'Informe uma data de nascimento válida');
        return;
      }

      const idade = new Date().getFullYear() - ano;

      if (idade > 120) {
        erroDataNascimento = true;
        alerts.exibirToast('warning', 'Informe uma data de nascimento válida');
      } else if (idade >= 16 && idade <= 17) {
        if (!this.aprovadorValido) {
          if (membro.cpfResponsavel == '') {
            erroDataNascimento = true;
            alerts.exibirToast('warning', 'Informe o CPF do responsável!');
          } else {
            this.ValidarResponsavel(membro);
            erroDataNascimento = true;
          }
        }
      } else if (idade < 16) {
        this.precisaAprovador = false;

        erroDataNascimento = true;
        alerts.exibirToast(
          'info',
          'Somente maiores de 16 anos podem cadastrar-se! Leia o regulamento.'
        );
      }
      if (erroDataNascimento) return;
    }

    if (etapa === 5) {
      const telefone = `55${membro.celular}`;
      if (telefone.length < 13) {
        this.etapaCadastro = 4;
        alerts.exibirToast('warning', 'O celular informado não é válido!');
        return;
      }

      const nascimento: string = membro.dataNascimento;
      const dia = Number(nascimento.substring(0, 2));
      const mes = Number(nascimento.substring(2, 4));
      const ano = Number(nascimento.substring(4, 8));

      var dataFinal = `${ano}/${mes}/${dia}`;

      const nascimentoDate: Date = new Date(dataFinal);

      let age = this.getAge(nascimentoDate);

      this.cadastroForm.controls['email'].setValidators([
        Validators.required,
        Validators.email,
      ]);

      if (membro.email !== '') {
        if (!membro.email.includes('.') || !membro.email.includes('@')) {
          alerts.exibirToast('warning', 'O e-mail informado não é válido!');
          return;
        }
      } else {
        if (age <= 65) {
          alerts.exibirToast('warning', 'Informe um e-mail para continuar!');
          return;
        } else {
          this.cadastroForm.controls['email'].clearValidators();
        }
      }
    }

    this.cadastroForm.controls['email'].updateValueAndValidity();
    this.etapaCadastro = etapa;
  }

  cadastrar() {
    let membro = this.cadastroForm.value;

    if (this.etapaCadastro === 5) {
      if (membro.senha !== membro.confirmarSenha) {
        alerts.exibirToast('warning', 'As senhas não conferem!');
        return;
      }
    }

    if (!membro.aceiteTermos) {
      alerts.exibirToast(
        'warning',
        'É preciso aceitar nossos termos e condições para fazer parte do nosso programa!'
      );
      return;
    }

    if (!this.cadastroForm.valid) return;

    const nascimento: string = membro.dataNascimento;
    const dia = Number(nascimento.substring(0, 2));
    const mes = Number(nascimento.substring(2, 4));
    const ano = Number(nascimento.substring(4, 8));

    var dataFinal = `${ano}/${mes}/${dia}`;

    const nascimentoDate: Date = new Date(dataFinal);

    const nome = `${membro.nome.trim()} ${membro.sobrenome.trim()}`;
    const telefone = `55${membro.celular}`;

    if (membro.email === '') membro.email = null;

    membro = {
      cpf: membro.cpf,
      aceiteTermo: membro.aceiteTermos,
      enviaComunicacao: true,
      nome: nome,
      email: membro.email,
      telefone: telefone,
      dataNascimento: nascimentoDate,
      senha: membro.senha,
      senhaConfirmacao: membro.confirmarSenha,
      genero: 'N',
      queroSerLover: true,
      origem: this.origem > 0 ? this.origem : 6,
      ativo: true,
      tipoStatus: 1,
      cpfResponsavel: membro.cpfResponsavel,
      hashIndicacao: this.hashIndicacao,
    };

    this.cadastrando = true;

    this.fidelidadeService.cadastrarMembro(membro).subscribe({
      next: (res: any) => this.processarSucessoCadastrarMembro(res),
      error: (err: any) => this.processarFalhaCadastrarMembro(err),
    });
  }

  private processarSucessoCadastrarMembro(res: any): void {
    const form = this.cadastroForm.value;

    this.etapaCadastro = 6;

    this.acessoForm = this.formBuilder.group({
      cpf: [form.cpf, [Validators.required, Validators.minLength(11)]],
      senha: [form.senha, [Validators.required]],
    });

    this.cadastrando = false;
  }

  private processarFalhaCadastrarMembro(err: any): void {
    console.error('Não foi possível cadastrar o membro', err);
    this.cadastrando = false;

    Swal.fire({
      icon: 'error',
      title: 'Não foi possível realizar o cadastro!',
      text: '',
      timerProgressBar: true,
      timer: 3000,
      showConfirmButton: false,
    });
  }

  getAge(birthDate: Date) {
    var today = new Date();
    var age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }


  public mostrarPopupTermos() {
    this.limparDescricao();
    this.exibirLoader = true;

    this.fidelidadeService.obterConfiguracaoPorDescricao('termos').subscribe(
      (sucesso) => {
        this.processarSucessoTermos(sucesso);
      },
      (falha) => {
        this.processarFalhaTermos(falha);
      }
    );
  }

  public mostrarPopupRegulamento() {
    this.limparDescricao();
    this.exibirLoader = true;

    this.fidelidadeService
      .obterConfiguracaoPorDescricao('regulamento')
      .subscribe(
        (sucesso) => {
          this.processarSucessoRegulamento(sucesso);
        },
        (falha) => {
          this.processarFalhaRegulamento(falha);
        }
      );
  }

  // ** METODOS PRIVADOS DE SUCESSO E INSUCESSO ** //
  private processarSucessoTermos(response: any) {
    let linhas = response.valor.split('\n');

    for (let index = 1; index < linhas.length; index++) {
      if (linhas[index] == '\r') continue;

      this.descricaoModalGenerico.push(`${linhas[index]}<br><br>`);
    }

    this.exibirLoader = false;

    this.exibePopupGenerico = true;
    this.tituloModalGenerico = linhas[0];
  }

  private processarFalhaTermos(fail: any) {
    this.exibirLoader = false;
    console.error('Ocorreu um erro ao tentar obter os termos', fail);
  }

  private processarSucessoRegulamento(response: any) {
    var linhas = response.valor.split('\n');

    for (let index = 0; index < linhas.length; index++) {
      if (linhas[index] == '\r') continue;

      this.descricaoModalGenerico.push(`${linhas[index]}<br><br>`);
    }

    this.exibirLoader = false;
    this.exibePopupGenerico = true;
    this.tituloModalGenerico = linhas[0];
  }

  private processarFalhaRegulamento(fail: any) {
    this.exibirLoader = false;
    console.error('Ocorreu um erro ao tentar obter o regulamento', fail);
  }

  // ** METODOS PRIVADOS DE APOIO ** //
  private limparDescricao() {
    this.descricaoModalGenerico = [];
  }

  trocarTipoCampoSenha(campoValidacao: string, icone: string) {
    if (campoValidacao === this.CAMPO_SENHA) {
      this.tipoCampoSenha = !this.tipoCampoSenha;
      this.iconeSenha = this.obterIconeSenha(icone);
    } else if (campoValidacao === this.CAMPO_CONFIRMACAO_SENHA) {
      this.tipoCampoConfirmacaoSenha = !this.tipoCampoConfirmacaoSenha;
      this.iconeConfirmacaoSenha = this.obterIconeSenha(icone);
    }
  }

  private obterIconeSenha(icone: string) {
    if (icone.includes(this.ICONE_OLHO_FECHADO)) return this.ICONE_OLHO_ABERTO;
    else return this.ICONE_OLHO_FECHADO;
  }

  ValidarResponsavel(membro: any) {
    this.aprovadorValido = false;
    this.exibirLoader = true;
    return new Promise((resolve, reject) => {
      this.fidelidadeService
        .obterDadosResponsavel(membro.cpfResponsavel)
        .subscribe({
          next: (res: any) => {
            if (
              res.tipoStatus != 2 &&
              res.tipoStatus != 6 &&
              res.tipoStatus != 7 &&
              res.tipoStatus != 8
            ) {
              const nascimentoResponsavel: string = res.dataNascimento;
              const anoResponsavel = Number(
                nascimentoResponsavel.substring(4, 8)
              );

              const idade = new Date().getFullYear() - anoResponsavel;

              this.exibirLoader = false;
              if (idade < 18)
                alerts.exibirToast(
                  'warning',
                  'Poxa... Seu responsável deve estar ativo no Cacau Lovers! Ele pode se cadastrar a qualquer momento e em seguida você poderá seguir!'
                );
              else {
                this.aprovadorValido = true;
                this.irParaEtapa(this.etapaCadastro + 1);
              }
            } else
              alerts.exibirToast(
                'warning',
                'Poxa... Seu responsável deve estar ativo no Cacau Lovers! Ele pode se cadastrar a qualquer momento e em seguida você poderá seguir!'
              );
            this.exibirLoader = false;
          },
          error: (res: any) => {
            alerts.exibirToast(
              'warning',
              'Poxa... Seu responsável deve estar ativo no Cacau Lovers! Ele pode se cadastrar a qualquer momento e em seguida você poderá seguir!'
            );
          },
        });
    });
  }

  validarDataNascimento(dataNascimento: any) {
    const nascimento: string = dataNascimento.target.value;
    const ano = Number(nascimento.substring(6, 10));

    if (ano.toString().length < 4) {
      alerts.exibirToast('warning', 'Informe uma data de nascimento válida');
      return;
    }

    const idade = new Date().getFullYear() - ano;

    if (idade >= 16 && idade <= 17) this.precisaAprovador = true;
    else this.precisaAprovador = false;
  }
}
